/**
 * Simple client-side router for ROCK AR
 * Handles navigation between AR experience and About page
 * Lightweight implementation without external dependencies
 */

export type Route = 'ar' | 'about'

export interface RouteChangeEvent {
  previousRoute: Route
  newRoute: Route
  timestamp: number
}

type RouteChangeCallback = (event: RouteChangeEvent) => void

export class Router {
  private currentRoute: Route = 'ar'
  private routeChangeCallbacks: RouteChangeCallback[] = []

  constructor() {
    // Initialize route from URL hash if present
    this.initializeFromURL()
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', () => {
      this.initializeFromURL()
    })
  }

  /**
   * Get current route
   */
  getCurrentRoute(): Route {
    return this.currentRoute
  }

  /**
   * Navigate to a route
   */
  navigateTo(route: Route): void {
    const previousRoute = this.currentRoute
    
    if (previousRoute === route) {
      return // Already on this route
    }

    this.currentRoute = route
    
    // Update URL hash for bookmarking and back button support
    if (route === 'about') {
      window.history.pushState({ route }, '', '#about')
    } else {
      window.history.pushState({ route }, '', '#')
    }

    // Emit route change event
    const event: RouteChangeEvent = {
      previousRoute,
      newRoute: route,
      timestamp: Date.now()
    }

    this.routeChangeCallbacks.forEach(callback => {
      try {
        callback(event)
      } catch (error) {
        console.error('Error in route change callback:', error)
      }
    })
  }

  /**
   * Register callback for route changes
   */
  onRouteChange(callback: RouteChangeCallback): () => void {
    this.routeChangeCallbacks.push(callback)
    
    // Return unsubscribe function
    return () => {
      const index = this.routeChangeCallbacks.indexOf(callback)
      if (index > -1) {
        this.routeChangeCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Initialize route from URL hash
   */
  private initializeFromURL(): void {
    const hash = window.location.hash
    
    if (hash === '#about') {
      this.currentRoute = 'about'
    } else {
      this.currentRoute = 'ar'
    }
  }

  /**
   * Navigate back to AR experience
   */
  navigateToAR(): void {
    this.navigateTo('ar')
  }

  /**
   * Navigate to About page
   */
  navigateToAbout(): void {
    this.navigateTo('about')
  }
}

// Singleton instance
let routerInstance: Router | null = null

/**
 * Get router singleton instance
 */
export function getRouter(): Router {
  if (!routerInstance) {
    routerInstance = new Router()
  }
  return routerInstance
}



